'use strict';

import advert from '../adverts/advert';
import config from '../utils/config';
import foundationLoader from '../utils/foundation-loader';
import layout from '../ui/layout';
import screenSizeWatcher from '../watchers/screen-size-watcher';
import videoPlayer from '../components/video-player';
import analytics from '../utils/analytics';
import tagManager from '../utils/tag-manager';

const classNames = {
	caption: 'caption',
	captionText: 'caption-text',
	controlButton: 'control-button',
	controls: 'controls',
	fullScreenGallery: 'full-screen-gallery',
	galleryLink: 'gallery-link',
	galleryPreview: 'gallery-preview',
	image: 'image',
	inlineImage: 'inline-image',
	mpuBlock: 'mpu',
	mpuContainer: 'mpu-container',
	remainingAdDuration: 'mpu__remaining-ad-duration',
	royalSliderOverflow: 'rsOverflow',
	slider: 'slider',
	sliderContainer: 'slider-container',
	videoSlide: 'video-slide',
	contactDealerSticky: 'contact-dealer__sticky-footer',
	contactDealerSlide: 'slider__contact-dealer',
	gridPadding: 'grid-padding-x',
};

const modifiers = {
	next: 'next',
	previous: 'prev',
	zoomIn: 'zoom-in',
	zoomOut: 'zoom-out',
	videoLoaded: 'video-loaded',
	inline: 'inline'
};

const trackingAction = {
	galleryOpened: 'Opened',
	galleryOpenedInline: 'Opened - Inline',
	imageViewed: 'Image viewed',
	imageViewedInline: 'Image viewed - Inline',
	slideViewed: 'Slide viewed',
	videoSlideViewed: 'Video slide viewed'
};

let $galleryInstance, slider, self;
let listenForIndexChange = true, isNavigationEnabled = true, openedFromInlineImage;
let initialIndex = 0;
let viewsSinceLastAd = 0, mobileAdDurationTimer, remainingMobileAdDuration = 0;
let placementHtml;

function getGalleryTemplateName() {
	return window.location.pathname.startsWith('/vans-pickups/') ? 'VanGallery' : 'CarGallery';
}

const analyticsEvents = {
	fireAnalyticsEvent: (action) => {
		const category = 'Full Screen Gallery';
		const makeAndModelName = tagManager.getMakeAndModelName();
		analytics.trackEvent(category, action, makeAndModelName);
	},

	fireAnalyticsPageView: (urlSegment) => {
		const gaPageOverride = config.dataLayer.page.galleryGaLocation;
		const galleryTemplateName = getGalleryTemplateName();

		// NOTE: this format of 'push' into the datalayer results in the
		// datalayer variable defined by the dot-notation-path being created/updated
		tagManager.pushToDataLayer({
			'page.templateName': galleryTemplateName
		});

		analytics.trackPageview(gaPageOverride + urlSegment + '/');

		// Revert to the underlying page's template name in case the value is required before a subsequent logView
		tagManager.pushToDataLayer({
			'page.templateName': config.dataLayer.page.template
		});

		analytics.trackIpsosIrisPageView();
	},

	triggerViewEvent: (slideIndex) => {
		if (gallerySlider.currentSlideIsAd()) {
			return;
		}

		// Timeout to give lazy-loaded content time to be populated before identifying slide type
		window.setTimeout(() => {
			if (gallerySlider.isVideoSlide()) {
				analyticsEvents.fireAnalyticsEvent(trackingAction.videoSlideViewed);
				analyticsEvents.fireAnalyticsPageView(`video/${slideIndex + 1}`);
				return;
			}

			// Try and get content slide name, and treat it as such if one is retrieved
			const contentSlideName = gallerySlider.getContentSlideName();
			if (contentSlideName) {
				analyticsEvents.fireAnalyticsEvent(trackingAction.slideViewed);
				analyticsEvents.fireAnalyticsPageView(contentSlideName);
				return;
			}

			// Treat as image if neither of the above. This is because even with an one second timeout
			// it still didnt always lazy-load the slide content fast enough to be identified as an image.
			analyticsEvents.fireAnalyticsEvent(openedFromInlineImage
				? trackingAction.imageViewedInline
				: trackingAction.imageViewed);
			analyticsEvents.fireAnalyticsPageView(slideIndex + 1);
			return;

		}, 1000);
	}
}

const eventListeners = {
	onBeforeIndexChange: (direction) => {
		const $panzoomContainer = gallerySlider.getActivePanzoomContainer();
		if ($panzoomContainer.length) {
			$panzoomContainer.panzoom('reset');
		}

		let relativeDirection = direction;
		if (relativeDirection !== 'next' && relativeDirection !== 'prev') {
			const currentSlideIndex = slider.currSlideId;
			relativeDirection = currentSlideIndex < direction ? 'next' : 'prev';
		}

		if (!layout.isDesktop() && config.galleryMobileAdFrequency > 0 && gallerySlider.adjacentSlideContainsAd(relativeDirection)) {
			galleryControls.disableNavigation();
		}
	},

	onIndexChange: () => {
		video.stop();
		gallerySlider.updateCaption();
		viewsSinceLastAd++;

		if (layout.isDesktop()) {
			eventListeners.onDesktopSlideChange();
		} else {
			eventListeners.onMobileSlideChange();
		}

		if (!gallerySlider.currentSlideIsAd()) {

			init.panzoom();
		}
	},

	onContentSet: () => {
		init.panzoom();
	},

	onDesktopSlideChange: () => {
		analyticsEvents.triggerViewEvent(slider.currSlideId);
		gallerySlider.toggleContactDealerButtons();
	},

	onMobileSlideChange: () => {
		if (config.galleryMobileAdFrequency > 0) {
			// Stop listening for index change events, they fire when dynamically adding & removing slides
			listenForIndexChange = false;

			if (gallerySlider.currentSlideIsAd()) {
				viewsSinceLastAd = 0;
				remainingMobileAdDuration = config.galleryMobileAdDuration;

				gallerySlider.removeAdSlides(slider.currSlideId);
				advert.loadGalleryAds();

				gallerySlider.updateAdSlideTimer();
				mobileAdDurationTimer = setInterval(function () {
					gallerySlider.updateAdSlideTimer();
				}, 1000);

				setTimeout(function () {
					galleryControls.enableNavigation();
				}, config.galleryMobileAdDuration * 1000);
			}
			else if (viewsSinceLastAd >= config.galleryMobileAdFrequency) {
				gallerySlider.appendAdSlide();
			}
			else {
				gallerySlider.removeAdSlides();
			}

			gallerySlider.toggleContactDealerButtons();

			listenForIndexChange = true;
		}

		analyticsEvents.triggerViewEvent(slider.currSlideId);
	}
};

const galleryControls = {
	disableNavigation: () => {
		slider.st.sliderDrag = false;
		slider.st.sliderTouch = false;
		isNavigationEnabled = false;

		$.findByBem(classNames.fullScreenGallery, classNames.slider).addClass('rsNoDrag');
		$.findByBem(classNames.fullScreenGallery, classNames.controls).slideUp();
	},

	enableNavigation: () => {
		slider.st.sliderDrag = true;
		slider.st.sliderTouch = true;
		isNavigationEnabled = true;

		$.findByBem(classNames.fullScreenGallery, classNames.slider).removeClass('rsNoDrag');
		$.findByBem(classNames.fullScreenGallery, classNames.controls).slideDown();
	},

	next: ($galleryControls) => {
		$galleryControls
			.findByBem(classNames.fullScreenGallery, classNames.controlButton, modifiers.next)
			.unbind()
			.on('click.full-screen-gallery-next', () => {
				if (isNavigationEnabled) {
					slider.next();
				}
			});
	},

	previous: ($galleryControls) => {
		$galleryControls
			.findByBem(classNames.fullScreenGallery, classNames.controlButton, modifiers.previous)
			.unbind()
			.on('click.full-screen-gallery-prev', () => {
				if (isNavigationEnabled) {
					slider.prev();
				}
			});
	},

	zoomIn: ($galleryControls) => {
		$galleryControls
			.findByBem(classNames.fullScreenGallery, classNames.controlButton, modifiers.zoomIn)
			.unbind()
			.on('click.full-screen-gallery-zoomIn', () => {
				const $panzoomContainer = gallerySlider.getActivePanzoomContainer();
				if ($panzoomContainer.length) {
					$panzoomContainer.panzoom('zoom');
				}
			});
	},

	zoomOut: ($galleryControls) => {
		$galleryControls
			.findByBem(classNames.fullScreenGallery, classNames.controlButton, modifiers.zoomOut)
			.unbind()
			.on('click.full-screen-gallery-zoomOut', () => {
				const $panzoomContainer = gallerySlider.getActivePanzoomContainer();
				if ($panzoomContainer.length) {
					$panzoomContainer.panzoom('zoom', true);
				}
			});
	}
}

const video = {
	markAsLoaded: () => {
		const $sliderContainer = video.getVideoContainer();
		$sliderContainer.addBemClass(classNames.fullScreenGallery, classNames.slider, modifiers.videoLoaded);
	},

	adjustForScreenSize: () => {
		const $sliderContainer = video.getVideoContainer();
		const $sliderOverflow = $($sliderContainer).find('.' + classNames.royalSliderOverflow);
		const videoContainerHeight = parseInt($sliderOverflow.css('height'), 10);
		const videoContainerWidth = parseInt($sliderOverflow.css('width'), 10);

		if (!video.shouldResize($sliderContainer)) {
			return;
		}

		const percentage = 0.75;
		const heightRatio = 9 / 16;
		const widthRatio = 16 / 9;
		let height = 0;
		let width = 0;

		const videoIFrameDimensions = video.getScaledDimensions(videoContainerHeight, videoContainerWidth, percentage);
		const requiredHeight = videoContainerWidth * heightRatio;

		if (requiredHeight > videoIFrameDimensions.availableHeight) {
			width = videoIFrameDimensions.availableHeight * widthRatio;
			height = videoIFrameDimensions.availableHeight;
		} else {
			width = videoIFrameDimensions.availableWidth;
			height = videoIFrameDimensions.availableWidth * heightRatio;
		}

		videoPlayer.resizeVideo(width, height);
		$.findByBem(classNames.fullScreenGallery, classNames.videoSlide)
			.addBemClass(classNames.fullScreenGallery, classNames.videoSlide, modifiers.videoLoaded);
	},

	getScaledDimensions: (videoContainerHeight, videoContainerWidth, percentage) => {
		return {
			availableHeight: videoContainerHeight * percentage,
			availableWidth: videoContainerWidth * percentage
		};
	},

	shouldResize: ($sliderContainer) => {
		return $sliderContainer.findByBem(classNames.fullScreenGallery, classNames.slider, modifiers.videoLoaded)
			&& $sliderContainer.is(':visible');
	},

	getVideoContainer: () => {
		return $.findByBem(classNames.fullScreenGallery, classNames.slider);
	},

	stop: () => {
		$.findByBem(classNames.fullScreenGallery, classNames.videoSlide).each(function() {
			const id = $(this).find('.video-player__video').attr('id');
			videoPlayer.stopVideo(id);
		});
	}
}

const init = {
	royalSlider: () => {
		gallerySlider.chooseImagesForScreenSize();
		const preloadImageCount = layout.isMobile() ? 2 : 4;
		
		$('.royalSlider').royalSlider({
			addActiveClass: true,
			arrowsNav: false,
			autoScaleSlider: true,
			controlNavigation: layout.isMobile() ? 'none' : 'thumbnails',
			controlsInside: false,
			fadeinLoadedSlide: true,
			globalCaption: true,
			globalCaptionInside: true,
			imageAlignCenter: true,
			imageScalePadding: 0,
			imageScaleMode: 'fit',
			keyboardNavEnabled: true,
			loop: true,
			navigateByClick: false,
			numImagesToPreload: preloadImageCount,
			startSlideId: initialIndex,
			usePreloader: true
		});
	},

	galleryEvents: () => {
		const sliderElement = $galleryInstance.findByBem(classNames.fullScreenGallery, classNames.slider);
		sliderElement.css('visibility', 'visible');

		slider = sliderElement.data('royalSlider');
		init.galleryControls();

		slider.ev.on('rsBeforeMove', (event, type) => {
			eventListeners.onBeforeIndexChange(type);
		});

		slider.ev.on('rsAfterSlideChange', () => {
			if (listenForIndexChange) {
				eventListeners.onIndexChange();
			}
		});

		slider.ev.on('rsAfterContentSet', () => {
			if (listenForIndexChange) {
				eventListeners.onContentSet();
			}
		});
	},

	galleryControls: () => {
		const $galleryControls = $galleryInstance.findByBem(classNames.fullScreenGallery, classNames.controls);
		galleryControls.previous($galleryControls);
		galleryControls.next($galleryControls);
		galleryControls.zoomIn($galleryControls);
		galleryControls.zoomOut($galleryControls);
	},

	panzoom: () => {
		let $panzoomContainer = gallerySlider.getActivePanzoomContainer();
		const activeImage = $('.rsActiveSlide > .rsImg', $galleryInstance);

		if ($panzoomContainer.length || !activeImage.length) {
			return;
		}

		import(/* webpackChunkName: "royal-slider" */ 'jquery.panzoom').then(() => {
			activeImage.wrap('<div class="panzoomContainer"></div>');
			$panzoomContainer = gallerySlider.getActivePanzoomContainer();

			$panzoomContainer.panzoom({
				cursor: 'grab',
				contain: 'automatic',
				increment: 0.3,
				minScale: 1,
				maxScale: 2,
				panOnlyWhenZoomed: true
			});

			import(/* webpackChunkName: "royal-slider" */ 'jquery.mousewheel').then(() => {
				$panzoomContainer.on('mousewheel', (event) => {
					event.preventDefault();
					const delta = event.delta || event.originalEvent.wheelDelta;
					const zoomOut = delta ? delta < 0 : event.originalEvent.deltaY > 0;
					$($panzoomContainer).panzoom('zoom', zoomOut, {
						increment: 0.1,
						focal: event
					});
				});
			});
		});
	},

	navigation: () => {
		slider.st.sliderDrag = true;
		slider.st.sliderTouch = true;
		isNavigationEnabled = true;

		$.findByBem(classNames.fullScreenGallery, classNames.slider).removeClass('rsNoDrag');
		$.findByBem(classNames.fullScreenGallery, classNames.controls).slideDown();
	},
	
	screenSizeWatcher: () => {
		screenSizeWatcher.onChange(() => {
			init.navigation();
			init.sliderSize();
			slider.updateSliderSize(true);
			video.adjustForScreenSize();
		});
	},

	sliderSize: () => {
		setTimeout(() => {
			slider.updateSliderSize(true);
		}, 10);
	},

	openGallery: (triggerElement) => {
		$(triggerElement).on('click.open-gallery', (event) => {
			const $targetLink = $(event.target).is('.' + classNames.galleryLink) ? $(event.target) : $(event.target).closestByBem(classNames.galleryLink);
			const galleryIndex = gallerySlider.getIndex($targetLink[0]);
			openedFromInlineImage = $targetLink.hasBemClass(classNames.galleryLink, null, modifiers.inline);
			event.stopPropagation();
			self.open(galleryIndex);
		});
	}
}

const gallerySlider = {
	getIndex: (openGalleryLink) => {
		const videoSlideIndex = 0;

		if (openGalleryLink.hasAttribute('watch-video') ||
			openGalleryLink.parentElement.hasAttribute('watch-video')) {
			return videoSlideIndex;
		}

		const hasVideo = $.findByBem(classNames.galleryPreview).data('has-video');
		const imageStartIndex = hasVideo ? 1 : 0;

		if (!openGalleryLink.hasAttribute('data-image-index')) {
			return imageStartIndex;
		}

		return parseInt(openGalleryLink.attributes['data-image-index'].value);
	},

	getActivePanzoomContainer: () => {
		return $('.rsActiveSlide > .panzoomContainer', $galleryInstance);
	},

	chooseImagesForScreenSize: () => {
		const screenSize = layout.getScreenSize();
		$('a.rsImg').each(() => {
			const link = $(this);
			const href = link.attr(`data-href-${screenSize}`);

			if (href) {
				link.attr('href', href);
			}
		});
	},

	moveToImageSlide: (imageIndex) => {
		gallerySlider.setTransitionSpeed(0);
		slider.goTo(imageIndex);
		gallerySlider.setTransitionSpeed();
		init.sliderSize();
	},

	setTransitionSpeed: (speed) => {
		const mobileSpeed = 300;
		const desktopSpeed = 600;

		const transitionSpeed = layout.isMobile() ? mobileSpeed : desktopSpeed;
		slider.st.transitionSpeed = speed ? speed : transitionSpeed;
	},

	updateCaption: () => {
		const $caption = $galleryInstance.findByBem(classNames.fullScreenGallery, classNames.caption);
		const captionText = $('.rsGCaption').text().trim();

		$caption.findByBem(classNames.fullScreenGallery, classNames.captionText).text(captionText);
		if (!captionText) {
			$caption.hide();
		} else if (!$caption.is(':visible')) {
			$caption.show();
		}
	},

	isVideoSlide: () => {
		return $('.rsActiveSlide .full-screen-gallery__video-slide').length > 0;
	},

	removeAdSlides: (retainIndexId) => {
		const adSlides = $.grep(slider.slides, function ($slide) {
			const $adContainer = $slide.content.filterByBem(classNames.fullScreenGallery, classNames.mpuBlock);
			return $adContainer.length > 0;
		});

		$.each(adSlides, function (index) {
			if (!retainIndexId || adSlides[index].id !== retainIndexId) {
				slider.removeSlide(adSlides[index].id);

				// Slide removed may have been before retained slide so the retain index id needs to be updated
				// If it was after it then this decrement doesnt matter as we've already processed the one we want to keep
				if (retainIndexId) {
					retainIndexId--;
				}
			}
		});
	},

	appendAdSlide: () => {
		gallerySlider.removeAdSlides();

		// append slide so an ad appears if navigated to previous
		slider.appendSlide(placementHtml, slider.currSlideId === 0 ? slider.numSlides + 1 : slider.currSlideId);

		// append slide so an ad appears if navigated to next
		slider.appendSlide(placementHtml, slider.currSlideId + 1);
	},

	updateAdSlideTimer: () => {
		let durationMessage = '';
		const $durationElement = $.findByBem(classNames.fullScreenGallery, classNames.remainingAdDuration);

		if (remainingMobileAdDuration > 0) {
			durationMessage = ' - You can skip in ' + remainingMobileAdDuration + 's';
		}
		else {
			window.clearInterval(mobileAdDurationTimer);
		}

		$durationElement.text(durationMessage);
		remainingMobileAdDuration--;
	},

	adjacentSlideContainsAd: (direction) => {
		const previousSlideId = slider.currSlideId === 0 ? slider.numSlides - 1 : slider.currSlideId - 1;
		const nextSlideId = slider.currSlideId + 1 === slider.numSlides ? 0 : slider.currSlideId + 1;

		if ((direction === "prev" && gallerySlider.slideHasClass(previousSlideId, $.getBemClass(classNames.fullScreenGallery, classNames.mpuBlock))) ||
			(direction === "next" && gallerySlider.slideHasClass(nextSlideId, $.getBemClass(classNames.fullScreenGallery, classNames.mpuBlock)))) {
			return true;
		}

		return false;
	},

	currentSlideIsAd: () => {
		return gallerySlider.slideHasClass(slider.currSlideId, $.getBemClass(classNames.fullScreenGallery, classNames.mpuBlock));
	},

	currentSlideIsContactDealer: () => {
		return gallerySlider.slideHasClass(slider.currSlideId, $.getBemClass(classNames.fullScreenGallery, classNames.contactDealerSlide));
	},

	slideHasClass: (id, cssClass) => {
		const container = slider.slides[id].content.filter('.' + cssClass);
		return container.length > 0;
	},

	getContentSlideName: () => {
		return $('.rsActiveSlide .rsContent').attr('data-ga-slide-name');
	},

	toggleContactDealerButtons: () => {
		// Hide the contact dealer buttons if the current slide is an ad or contact dealer slide
		if (gallerySlider.currentSlideIsContactDealer() || gallerySlider.currentSlideIsAd()) {
			$.findByBem(classNames.contactDealerSticky).hide();
		}
		else {
			$.findByBem(classNames.contactDealerSticky).show();
		}
	}
};

export default {
	init() {
		foundationLoader.addPlugins(foundationLoader.PLUGINS.REVEAL);
		$galleryInstance = $.findByBem(classNames.fullScreenGallery);
		placementHtml = $galleryInstance.findByBem(classNames.fullScreenGallery, classNames.mpuContainer).html();

		this.setOpenCloseGallery();
		this.initOpenGallery();

		$(document).on('video.injected', () => {
			video.markAsLoaded();
			video.adjustForScreenSize();
		});
	},

	initOpenGallery(triggerElement) {
		self = this;
		if (triggerElement) {
			init.openGallery(triggerElement);
			return;
		}

		$.findByBem(classNames.galleryLink).each((index, element) => {
			init.openGallery(element);
		});
	},

	setOpenCloseGallery() {
		$('#fullScreenGallery')
			.on('open.zf.reveal', () => {
				const templateName = getGalleryTemplateName();
				advert.updateCmsTemplateName(templateName);

				if (slider) {
					// If the requested index is the same as the current one then manually log the image view.
					// If the gallery is already init'd it will fire an onIndexChange event if the index is different.
					if (slider.currSlideId === initialIndex) {
						analyticsEvents.triggerViewEvent(slider.currSlideId);
					}

					if (layout.isIOS()) {
						window.setTimeout(function () {
							// Workaround for apparent race condition in iOS where slide is not displayed after opening
							gallerySlider.moveToImageSlide(initialIndex);
						}, 250);
					} else {
						gallerySlider.moveToImageSlide(initialIndex);
					}

					return;
				}

				import(/* webpackChunkName: "royal-slider" */ '../vendor/royal-slider').then(() => {
					init.royalSlider();
					init.galleryEvents();
					init.panzoom();
					init.screenSizeWatcher();
					init.sliderSize();

					video.adjustForScreenSize();
					gallerySlider.updateCaption();
					gallerySlider.setTransitionSpeed();

					analyticsEvents.triggerViewEvent(slider.currSlideId);
				});
			})
			.on('closed.zf.reveal', () => {
				advert.updateCmsTemplateName();
				video.stop();
			});
	},

	open(index) {
		initialIndex = index || 0;
		$galleryInstance.foundation('open');
		analyticsEvents.fireAnalyticsEvent(openedFromInlineImage
			? trackingAction.galleryOpenedInline
			: trackingAction.galleryOpened);

		advert.loadGalleryAds();
		
		viewsSinceLastAd = 1;
	}
}